import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import useDashboardStore from '../store/dashboardStore';
import { formatCurrency, formatPercent } from '../utils/formatters';
import { getDay, format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subMonths, subWeeks, getWeek, getYear } from 'date-fns';

const { FiTrendingUp, FiTrendingDown, FiMinus, FiBarChart3, FiCalendar, FiEye, FiEyeOff, FiTarget, FiAlertTriangle, FiTrendingDown: FiAlert, FiRepeat, FiClock } = FiIcons;

const PerformanceSummary = () => {
  const { 
    preset, dateFrom, dateTo, compare, kpis, hasData, loading, 
    seriesData, rankedData, topBottomMetric 
  } = useDashboardStore();
  
  const [analysisData, setAnalysisData] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [extendedAnalysis, setExtendedAnalysis] = useState(null);
  const [comparativeAnalysis, setComparativeAnalysis] = useState(null);

  // Generate performance analysis based on current data and preset
  useEffect(() => {
    if (hasData && kpis && !loading) {
      generateAnalysis();
    }
  }, [hasData, kpis, preset, dateFrom, dateTo, compare, seriesData, rankedData]);

  // Enhanced analysis generation with comprehensive DoW vs DoW and WoW vs WoW analysis
  const generateAnalysis = async () => {
    setAnalyzing(true);
    
    try {
      // Get key metrics for analysis
      const revenue = kpis.revenue.value;
      const revenueDelta = kpis.revenue.delta;
      const revenueDeltaPercent = kpis.revenue.deltaPercent;
      const profitRatio = kpis.profitRatio.value;
      const profitDelta = kpis.profitRatio.delta;
      
      // Get top performers for insights
      const topPerformers = rankedData?.slice(0, 3) || [];
      const bottomPerformers = rankedData?.slice(-2) || [];
      
      // Determine trend direction and magnitude
      const getTrendInfo = () => {
        if (revenueDelta === null) return { direction: 'stable', magnitude: 'no-data' };
        
        const absPercent = Math.abs(revenueDeltaPercent || 0);
        let magnitude;
        if (absPercent < 5) magnitude = 'slight';
        else if (absPercent < 15) magnitude = 'moderate';
        else if (absPercent < 30) magnitude = 'significant';
        else magnitude = 'dramatic';
        
        const direction = revenueDelta > 0 ? 'up' : revenueDelta < 0 ? 'down' : 'stable';
        return { direction, magnitude, percent: absPercent };
      };

      const trend = getTrendInfo();
      
      // ENHANCED: Generate DoW vs DoW and WoW vs WoW comparative analysis
      let comparativeAnalysisData = null;
      if (compare === 'DOW' && preset === 'DATE' && dateFrom === dateTo) {
        comparativeAnalysisData = await generateDowVsDowAnalysis(dateFrom, revenue, profitRatio, trend, topPerformers);
      } else if (compare === 'WOW' && preset === 'WEEK') {
        comparativeAnalysisData = await generateWowVsWowAnalysis(dateFrom, dateTo, revenue, profitRatio, trend, topPerformers);
      }
      
      // ENHANCED: Comprehensive DATE preset analysis
      let extendedDateAnalysis = null;
      if (preset === 'DATE' && dateFrom === dateTo) {
        extendedDateAnalysis = await generateExtendedDateAnalysis(dateFrom, revenue, profitRatio, trend, topPerformers);
      }
      
      // Get comparison context based on preset
      const getComparisonContext = () => {
        switch (preset) {
          case 'DATE':
            if (compare === 'DOW') {
              const selectedDate = new Date(dateFrom);
              const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
              const dayName = dayNames[getDay(selectedDate)];
              return `vs. 12 prior ${dayName}s`;
            }
            return 'vs. 7-day average';
          case 'WEEK':
            return compare === 'WOW' ? 'vs. 12 prior weeks' : 'vs. previous week';
          case 'MTD':
            return 'vs. previous month';
          case 'YTD':
            return 'vs. previous year period';
          case 'CUSTOM':
            return compare === 'WOW' ? 'vs. 12 Sunday-week average' : 'vs. comparison period';
          default:
            return 'vs. comparison period';
        }
      };

      const comparisonContext = getComparisonContext();
      
      // Generate analysis text with enhanced DoW vs DoW and WoW vs WoW content
      const generateAnalysisText = () => {
        const dateContext = preset === 'DATE' && dateFrom === dateTo 
          ? format(new Date(dateFrom), 'MMMM d, yyyy')
          : `${dateFrom} to ${dateTo}`;
        
        // Enhanced analysis for DoW vs DoW comparison
        if (compare === 'DOW' && preset === 'DATE' && dateFrom === dateTo && comparativeAnalysisData) {
          return generateComprehensiveDowAnalysis(dateContext, revenue, profitRatio, trend, comparisonContext, topPerformers, comparativeAnalysisData);
        }
        
        // Enhanced analysis for WoW vs WoW comparison
        if (compare === 'WOW' && preset === 'WEEK' && comparativeAnalysisData) {
          return generateComprehensiveWowAnalysis(dateContext, revenue, profitRatio, trend, comparisonContext, topPerformers, comparativeAnalysisData);
        }
        
        // Enhanced analysis for DATE preset
        if (preset === 'DATE' && dateFrom === dateTo && extendedDateAnalysis) {
          return generateComprehensiveDateAnalysis(dateContext, revenue, profitRatio, trend, comparisonContext, topPerformers, extendedDateAnalysis);
        }
        
        // Standard analysis for other presets
        let analysis = '';
        
        if (trend.direction === 'stable' || trend.magnitude === 'no-data') {
          analysis = `Performance for ${dateContext} shows stable results with ${formatCurrency(revenue)} in revenue. `;
        } else {
          const trendWord = trend.direction === 'up' ? 'strong' : 'challenging';
          const changeText = trend.direction === 'up' ? 'increase' : 'decline';
          
          analysis = `Performance for ${dateContext} shows ${trendWord} results with ${formatCurrency(revenue)} in revenue, `;
          analysis += `representing a ${trend.magnitude} ${changeText} of ${formatPercent(trend.percent)} ${comparisonContext}. `;
        }
        
        // Add profit margin context
        if (profitDelta !== null) {
          const profitTrend = profitDelta > 0 ? 'improved' : profitDelta < 0 ? 'compressed' : 'maintained';
          analysis += `Profit margins ${profitTrend} to ${formatPercent(profitRatio)}, `;
          
          if (Math.abs(profitDelta) > 2) {
            const profitDirection = profitDelta > 0 ? 'contributing positively' : 'creating headwinds';
            analysis += `${profitDirection} to overall performance. `;
          } else {
            analysis += 'remaining relatively stable. ';
          }
        }
        
        // Add product performance insights
        if (topPerformers.length > 0) {
          const topProduct = topPerformers[0];
          const topProductShare = topProduct.percent;
          
          if (topProductShare > 20) {
            analysis += `${topProduct.name} dominated with ${formatPercent(topProductShare)} of ${topBottomMetric}, `;
            
            if (topPerformers.length > 1) {
              const secondProduct = topPerformers[1];
              analysis += `while ${secondProduct.name} contributed ${formatPercent(secondProduct.percent)}. `;
            }
          } else {
            analysis += `Performance was well-distributed with ${topProduct.name} leading at ${formatPercent(topProductShare)} of ${topBottomMetric}. `;
          }
        }
        
        return analysis.trim();
      };

      const analysisText = generateAnalysisText();
      
      setAnalysisData({
        text: analysisText,
        trend: trend,
        revenue: revenue,
        revenueDelta: revenueDelta,
        revenueDeltaPercent: revenueDeltaPercent,
        profitRatio: profitRatio,
        profitDelta: profitDelta,
        comparisonContext: comparisonContext,
        topPerformer: topPerformers[0] || null,
        dateContext: preset === 'DATE' && dateFrom === dateTo 
          ? format(new Date(dateFrom), 'MMMM d, yyyy')
          : `${dateFrom} to ${dateTo}`
      });
      
      setExtendedAnalysis(extendedDateAnalysis);
      setComparativeAnalysis(comparativeAnalysisData);
      
    } catch (error) {
      console.error('Error generating analysis:', error);
      setAnalysisData({
        text: 'Unable to generate performance analysis at this time. Please check your data and try again.',
        trend: { direction: 'stable', magnitude: 'no-data' }
      });
    }
    
    setAnalyzing(false);
  };

  // NEW: Generate comprehensive DoW vs DoW analysis with 12 prior similar DoWs
  const generateDowVsDowAnalysis = async (selectedDate, revenue, profitRatio, trend, topPerformers) => {
    try {
      console.log('📊 Generating comprehensive DoW vs DoW analysis with 12 prior similar DoWs...');
      
      const targetDate = new Date(selectedDate);
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const dayOfWeek = getDay(targetDate);
      const dayName = dayNames[dayOfWeek];
      
      // Calculate historical performance metrics from seriesData
      const comparisonDays = seriesData?.filter(d => d.isComparison) || [];
      const selectedDayData = seriesData?.find(d => d.isSelected) || null;
      
      if (comparisonDays.length === 0 || !selectedDayData) {
        return null;
      }
      
      // Analyze the 12 prior similar DoWs
      const historicalRevenues = comparisonDays.map(d => d.revenue).filter(r => r > 0);
      const avgHistoricalRevenue = historicalRevenues.length > 0 ? 
        historicalRevenues.reduce((sum, r) => sum + r, 0) / historicalRevenues.length : 0;
      
      const maxHistoricalRevenue = Math.max(...historicalRevenues);
      const minHistoricalRevenue = Math.min(...historicalRevenues);
      
      // Calculate percentile ranking
      const sortedHistoricalRevenues = [...historicalRevenues].sort((a, b) => a - b);
      const currentRank = sortedHistoricalRevenues.filter(r => r <= revenue).length;
      const percentileRank = historicalRevenues.length > 0 ? 
        Math.round((currentRank / historicalRevenues.length) * 100) : 50;
      
      // Determine performance category based on percentile
      let performanceCategory = 'average';
      let performanceDescription = '';
      
      if (percentileRank >= 90) {
        performanceCategory = 'exceptional';
        performanceDescription = `top 10% of ${dayName}s`;
      } else if (percentileRank >= 75) {
        performanceCategory = 'strong';
        performanceDescription = `top 25% of ${dayName}s`;
      } else if (percentileRank >= 50) {
        performanceCategory = 'above-average';
        performanceDescription = `above-average for ${dayName}s`;
      } else if (percentileRank >= 25) {
        performanceCategory = 'below-average';
        performanceDescription = `below-average for ${dayName}s`;
      } else {
        performanceCategory = 'concerning';
        performanceDescription = `bottom 25% of ${dayName}s`;
      }
      
      // Calculate volatility (standard deviation)
      const variance = historicalRevenues.length > 1 ? 
        historicalRevenues.reduce((sum, r) => sum + Math.pow(r - avgHistoricalRevenue, 2), 0) / (historicalRevenues.length - 1) : 0;
      const standardDeviation = Math.sqrt(variance);
      const coefficientOfVariation = avgHistoricalRevenue > 0 ? (standardDeviation / avgHistoricalRevenue) * 100 : 0;
      
      // Determine consistency pattern
      let consistencyPattern = 'stable';
      if (coefficientOfVariation > 30) {
        consistencyPattern = 'highly-volatile';
      } else if (coefficientOfVariation > 15) {
        consistencyPattern = 'moderately-volatile';
      } else if (coefficientOfVariation < 5) {
        consistencyPattern = 'very-stable';
      }
      
      // Seasonal trends (if enough data)
      const recentDoWs = comparisonDays.slice(0, 6); // Last 6 similar DoWs
      const olderDoWs = comparisonDays.slice(6); // Older similar DoWs
      
      let seasonalTrend = 'neutral';
      if (recentDoWs.length >= 3 && olderDoWs.length >= 3) {
        const recentAvg = recentDoWs.reduce((sum, d) => sum + d.revenue, 0) / recentDoWs.length;
        const olderAvg = olderDoWs.reduce((sum, d) => sum + d.revenue, 0) / olderDoWs.length;
        
        if (recentAvg > olderAvg * 1.1) {
          seasonalTrend = 'improving';
        } else if (recentAvg < olderAvg * 0.9) {
          seasonalTrend = 'declining';
        }
      }
      
      return {
        dayName,
        dayOfWeek,
        comparisonCount: comparisonDays.length,
        avgHistoricalRevenue,
        maxHistoricalRevenue,
        minHistoricalRevenue,
        percentileRank,
        performanceCategory,
        performanceDescription,
        standardDeviation,
        coefficientOfVariation,
        consistencyPattern,
        seasonalTrend,
        recentDoWCount: recentDoWs.length,
        olderDoWCount: olderDoWs.length
      };
      
    } catch (error) {
      console.error('Error generating DoW vs DoW analysis:', error);
      return null;
    }
  };

  // NEW: Generate comprehensive WoW vs WoW analysis with 12 prior weeks
  const generateWowVsWowAnalysis = async (dateFrom, dateTo, revenue, profitRatio, trend, topPerformers) => {
    try {
      console.log('📊 Generating comprehensive WoW vs WoW analysis with 12 prior weeks...');
      
      // Calculate week information
      const startDate = new Date(dateFrom);
      const endDate = new Date(dateTo);
      const currentWeekNumber = getWeek(startDate, { weekStartsOn: 1 });
      const currentYear = getYear(startDate);
      
      // Analyze historical performance from seriesData
      const currentWeekData = seriesData?.find(d => d.isCurrent) || null;
      const comparisonWeeks = seriesData?.filter(d => !d.isCurrent && d.weekLabel) || [];
      
      if (comparisonWeeks.length === 0 || !currentWeekData) {
        return null;
      }
      
      // Analyze the 12 prior weeks
      const historicalRevenues = comparisonWeeks.map(d => d.revenue).filter(r => r > 0);
      const avgHistoricalRevenue = historicalRevenues.length > 0 ? 
        historicalRevenues.reduce((sum, r) => sum + r, 0) / historicalRevenues.length : 0;
      
      const maxHistoricalRevenue = Math.max(...historicalRevenues);
      const minHistoricalRevenue = Math.min(...historicalRevenues);
      
      // Calculate percentile ranking
      const sortedHistoricalRevenues = [...historicalRevenues].sort((a, b) => a - b);
      const currentRank = sortedHistoricalRevenues.filter(r => r <= revenue).length;
      const percentileRank = historicalRevenues.length > 0 ? 
        Math.round((currentRank / historicalRevenues.length) * 100) : 50;
      
      // Determine performance category
      let performanceCategory = 'average';
      let performanceDescription = '';
      
      if (percentileRank >= 90) {
        performanceCategory = 'exceptional';
        performanceDescription = 'top 10% of recent weeks';
      } else if (percentileRank >= 75) {
        performanceCategory = 'strong';
        performanceDescription = 'top 25% of recent weeks';
      } else if (percentileRank >= 50) {
        performanceCategory = 'above-average';
        performanceDescription = 'above-average weekly performance';
      } else if (percentileRank >= 25) {
        performanceCategory = 'below-average';
        performanceDescription = 'below-average weekly performance';
      } else {
        performanceCategory = 'concerning';
        performanceDescription = 'bottom 25% of recent weeks';
      }
      
      // Calculate weekly volatility
      const variance = historicalRevenues.length > 1 ? 
        historicalRevenues.reduce((sum, r) => sum + Math.pow(r - avgHistoricalRevenue, 2), 0) / (historicalRevenues.length - 1) : 0;
      const standardDeviation = Math.sqrt(variance);
      const coefficientOfVariation = avgHistoricalRevenue > 0 ? (standardDeviation / avgHistoricalRevenue) * 100 : 0;
      
      // Weekly consistency pattern
      let weeklyConsistencyPattern = 'stable';
      if (coefficientOfVariation > 25) {
        weeklyConsistencyPattern = 'highly-volatile';
      } else if (coefficientOfVariation > 12) {
        weeklyConsistencyPattern = 'moderately-volatile';
      } else if (coefficientOfVariation < 6) {
        weeklyConsistencyPattern = 'very-stable';
      }
      
      // Quarterly trend analysis
      const recentWeeks = comparisonWeeks.slice(0, 6); // Last 6 weeks
      const olderWeeks = comparisonWeeks.slice(6); // Older weeks
      
      let quarterlyTrend = 'neutral';
      if (recentWeeks.length >= 3 && olderWeeks.length >= 3) {
        const recentAvg = recentWeeks.reduce((sum, d) => sum + d.revenue, 0) / recentWeeks.length;
        const olderAvg = olderWeeks.reduce((sum, d) => sum + d.revenue, 0) / olderWeeks.length;
        
        if (recentAvg > olderAvg * 1.1) {
          quarterlyTrend = 'improving';
        } else if (recentAvg < olderAvg * 0.9) {
          quarterlyTrend = 'declining';
        }
      }
      
      // Peak vs trough analysis
      const revenueRange = maxHistoricalRevenue - minHistoricalRevenue;
      const currentPositionInRange = maxHistoricalRevenue > minHistoricalRevenue ? 
        (revenue - minHistoricalRevenue) / revenueRange : 0.5;
      
      let rangePosition = 'middle';
      if (currentPositionInRange >= 0.8) {
        rangePosition = 'near-peak';
      } else if (currentPositionInRange <= 0.2) {
        rangePosition = 'near-trough';
      } else if (currentPositionInRange >= 0.6) {
        rangePosition = 'upper-range';
      } else if (currentPositionInRange <= 0.4) {
        rangePosition = 'lower-range';
      }
      
      return {
        currentWeekNumber,
        currentYear,
        comparisonCount: comparisonWeeks.length,
        avgHistoricalRevenue,
        maxHistoricalRevenue,
        minHistoricalRevenue,
        percentileRank,
        performanceCategory,
        performanceDescription,
        standardDeviation,
        coefficientOfVariation,
        weeklyConsistencyPattern,
        quarterlyTrend,
        recentWeekCount: recentWeeks.length,
        olderWeekCount: olderWeeks.length,
        rangePosition,
        currentPositionInRange
      };
      
    } catch (error) {
      console.error('Error generating WoW vs WoW analysis:', error);
      return null;
    }
  };

  // ENHANCED: Generate comprehensive analysis specifically for DATE preset
  const generateExtendedDateAnalysis = async (selectedDate, revenue, profitRatio, trend, topPerformers) => {
    try {
      console.log('📊 Generating extended DATE analysis for comprehensive perspective...');
      
      const targetDate = new Date(selectedDate);
      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const dayOfWeek = getDay(targetDate);
      const dayName = dayNames[dayOfWeek];
      
      // Calculate various time periods for context
      const weekStart = startOfWeek(targetDate, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(targetDate, { weekStartsOn: 1 });
      const monthStart = startOfMonth(targetDate);
      const monthEnd = endOfMonth(targetDate);
      const lastMonthStart = startOfMonth(subMonths(targetDate, 1));
      const lastMonthEnd = endOfMonth(subMonths(targetDate, 1));
      
      // Determine business context
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      const isMonthStart = targetDate.getDate() <= 7;
      const isMonthEnd = targetDate.getDate() > 22;
      const isQuarterEnd = [2, 5, 8, 11].includes(targetDate.getMonth()) && isMonthEnd;
      
      // Performance categorization
      let performanceCategory = 'standard';
      let performanceInsight = '';
      
      if (trend.direction === 'up' && trend.percent > 20) {
        performanceCategory = 'exceptional';
        performanceInsight = 'exceptional performance day';
      } else if (trend.direction === 'up' && trend.percent > 10) {
        performanceCategory = 'strong';
        performanceInsight = 'strong performance day';
      } else if (trend.direction === 'down' && trend.percent > 20) {
        performanceCategory = 'concerning';
        performanceInsight = 'concerning underperformance';
      } else if (trend.direction === 'down' && trend.percent > 10) {
        performanceCategory = 'weak';
        performanceInsight = 'below-average performance';
      } else {
        performanceCategory = 'normal';
        performanceInsight = 'typical performance range';
      }
      
      // Business day context
      let businessContext = '';
      if (isWeekend) {
        businessContext = isWeekend && performanceCategory === 'exceptional' 
          ? 'remarkable for a weekend day' 
          : isWeekend && performanceCategory === 'concerning'
          ? 'particularly weak for weekend operations'
          : 'aligns with weekend patterns';
      } else {
        businessContext = performanceCategory === 'exceptional'
          ? 'outstanding for a weekday'
          : performanceCategory === 'concerning'
          ? 'unexpectedly low for weekday operations'
          : 'consistent with weekday expectations';
      }
      
      // Seasonal and timing insights
      const monthName = format(targetDate, 'MMMM');
      const isHolidayMonth = [11, 0].includes(targetDate.getMonth()); // Dec, Jan
      const isSummerMonth = [5, 6, 7].includes(targetDate.getMonth()); // Jun, Jul, Aug
      
      let seasonalContext = '';
      if (isHolidayMonth) {
        seasonalContext = performanceCategory === 'exceptional' 
          ? 'capitalizing on holiday season demand' 
          : performanceCategory === 'concerning'
          ? 'missing holiday season opportunities'
          : 'maintaining holiday season momentum';
      } else if (isSummerMonth) {
        seasonalContext = performanceCategory === 'strong' 
          ? 'leveraging summer customer activity' 
          : 'navigating typical summer patterns';
      } else {
        seasonalContext = 'operating within seasonal norms';
      }
      
      // Product mix analysis
      let productMixInsight = '';
      if (topPerformers.length > 0) {
        const topProduct = topPerformers[0];
        const concentration = topProduct.percent;
        
        if (concentration > 40) {
          productMixInsight = `heavily concentrated in ${topProduct.name} (${formatPercent(concentration)}), suggesting strong customer preference but potential risk in product diversification`;
        } else if (concentration > 25) {
          productMixInsight = `led by ${topProduct.name} (${formatPercent(concentration)}) with healthy supporting products, indicating balanced customer appeal`;
        } else {
          productMixInsight = `well-diversified across products with ${topProduct.name} leading (${formatPercent(concentration)}), demonstrating broad market appeal`;
        }
      }
      
      // Profit margin context
      let profitabilityInsight = '';
      if (profitRatio > 45) {
        profitabilityInsight = 'exceptional profitability suggesting premium positioning and efficient operations';
      } else if (profitRatio > 35) {
        profitabilityInsight = 'healthy profit margins indicating good operational control';
      } else if (profitRatio > 25) {
        profitabilityInsight = 'acceptable margins with room for optimization';
      } else if (profitRatio > 15) {
        profitabilityInsight = 'compressed margins requiring attention to cost structure or pricing';
      } else {
        profitabilityInsight = 'concerning margins demanding immediate operational review';
      }
      
      return {
        dayName,
        dayOfWeek,
        isWeekend,
        performanceCategory,
        performanceInsight,
        businessContext,
        seasonalContext,
        productMixInsight,
        profitabilityInsight,
        monthName,
        isHolidayMonth,
        isSummerMonth,
        isMonthStart,
        isMonthEnd,
        isQuarterEnd
      };
      
    } catch (error) {
      console.error('Error generating extended DATE analysis:', error);
      return null;
    }
  };

  // NEW: Generate comprehensive DoW vs DoW analysis text
  const generateComprehensiveDowAnalysis = (dateContext, revenue, profitRatio, trend, comparisonContext, topPerformers, dowAnalysis) => {
    if (!dowAnalysis) return 'DoW vs DoW analysis unavailable.';
    
    let analysis = '';
    
    // Opening with comprehensive DoW context
    analysis += `${dateContext} (${dowAnalysis.dayName}) delivered ${dowAnalysis.performanceDescription} with ${formatCurrency(revenue)} in revenue, `;
    
    // Historical ranking and percentile context
    analysis += `ranking in the ${dowAnalysis.percentileRank}th percentile among ${dowAnalysis.comparisonCount} similar ${dowAnalysis.dayName}s analyzed. `;
    
    // Trend and comparison context
    if (trend.direction !== 'stable' && trend.magnitude !== 'no-data') {
      analysis += `This represents a ${trend.magnitude} ${trend.direction === 'up' ? 'increase' : 'decline'} of ${formatPercent(trend.percent)} compared to the ${dowAnalysis.comparisonCount}-${dowAnalysis.dayName} average of ${formatCurrency(dowAnalysis.avgHistoricalRevenue)}. `;
    } else {
      analysis += `Performance aligns closely with the historical ${dowAnalysis.dayName} average of ${formatCurrency(dowAnalysis.avgHistoricalRevenue)}. `;
    }
    
    // Historical range and volatility context
    analysis += `Historical ${dowAnalysis.dayName} performance has ranged from ${formatCurrency(dowAnalysis.minHistoricalRevenue)} to ${formatCurrency(dowAnalysis.maxHistoricalRevenue)}, `;
    analysis += `demonstrating ${dowAnalysis.consistencyPattern.replace('-', ' ')} patterns with ${formatPercent(dowAnalysis.coefficientOfVariation)} revenue volatility. `;
    
    // Seasonal trend insights
    if (dowAnalysis.seasonalTrend === 'improving') {
      analysis += `Recent ${dowAnalysis.dayName}s show an improving trend, indicating strengthening customer engagement on this day of the week. `;
    } else if (dowAnalysis.seasonalTrend === 'declining') {
      analysis += `Recent ${dowAnalysis.dayName}s show a declining pattern, suggesting potential opportunities to reinvigorate ${dowAnalysis.dayName} performance. `;
    } else {
      analysis += `${dowAnalysis.dayName} performance has remained consistent over recent periods, indicating stable customer patterns. `;
    }
    
    // Profitability context
    analysis += `Profit margins of ${formatPercent(profitRatio)} `;
    if (dowAnalysis.performanceCategory === 'exceptional' || dowAnalysis.performanceCategory === 'strong') {
      analysis += `complement the strong revenue performance, suggesting effective operational execution on high-performing ${dowAnalysis.dayName}s. `;
    } else if (dowAnalysis.performanceCategory === 'concerning') {
      analysis += `require attention alongside the revenue concerns to restore ${dowAnalysis.dayName} profitability. `;
    } else {
      analysis += `align with typical ${dowAnalysis.dayName} operational patterns. `;
    }
    
    // Strategic implications
    if (dowAnalysis.performanceCategory === 'exceptional') {
      analysis += `This top-tier ${dowAnalysis.dayName} performance suggests successful strategies that should be analyzed and replicated across other days of the week.`;
    } else if (dowAnalysis.performanceCategory === 'concerning') {
      analysis += `The bottom-quartile ${dowAnalysis.dayName} performance warrants immediate investigation into day-specific factors affecting customer engagement and operational efficiency.`;
    } else if (dowAnalysis.performanceCategory === 'strong') {
      analysis += `The above-average ${dowAnalysis.dayName} results indicate effective day-specific execution with opportunities to optimize for even stronger performance.`;
    } else {
      analysis += `The consistent ${dowAnalysis.dayName} performance provides a stable foundation with potential for targeted improvements based on top-performing ${dowAnalysis.dayName} patterns.`;
    }
    
    return analysis.trim();
  };

  // NEW: Generate comprehensive WoW vs WoW analysis text
  const generateComprehensiveWowAnalysis = (dateContext, revenue, profitRatio, trend, comparisonContext, topPerformers, wowAnalysis) => {
    if (!wowAnalysis) return 'WoW vs WoW analysis unavailable.';
    
    let analysis = '';
    
    // Opening with comprehensive WoW context
    analysis += `Week ${wowAnalysis.currentWeekNumber} ${wowAnalysis.currentYear} (${dateContext}) achieved ${wowAnalysis.performanceDescription} with ${formatCurrency(revenue)} in revenue, `;
    
    // Historical ranking and percentile context
    analysis += `ranking in the ${wowAnalysis.percentileRank}th percentile among the ${wowAnalysis.comparisonCount} weeks analyzed. `;
    
    // Trend and comparison context
    if (trend.direction !== 'stable' && trend.magnitude !== 'no-data') {
      analysis += `This marks a ${trend.magnitude} ${trend.direction === 'up' ? 'improvement' : 'decline'} of ${formatPercent(trend.percent)} compared to the 12-week average of ${formatCurrency(wowAnalysis.avgHistoricalRevenue)}. `;
    } else {
      analysis += `Performance tracks closely with the 12-week average of ${formatCurrency(wowAnalysis.avgHistoricalRevenue)}. `;
    }
    
    // Historical range and position context
    analysis += `Over the analyzed period, weekly revenue has ranged from ${formatCurrency(wowAnalysis.minHistoricalRevenue)} to ${formatCurrency(wowAnalysis.maxHistoricalRevenue)}, `;
    analysis += `with current performance positioned in the ${wowAnalysis.rangePosition.replace('-', ' ')} of this range. `;
    
    // Weekly volatility and consistency patterns
    analysis += `Weekly performance demonstrates ${wowAnalysis.weeklyConsistencyPattern.replace('-', ' ')} patterns with ${formatPercent(wowAnalysis.coefficientOfVariation)} revenue volatility. `;
    
    // Quarterly trend insights
    if (wowAnalysis.quarterlyTrend === 'improving') {
      analysis += `Recent weeks show an upward trajectory, indicating strengthening business momentum and growing market traction. `;
    } else if (wowAnalysis.quarterlyTrend === 'declining') {
      analysis += `Recent weeks reveal a concerning downward trend, highlighting the need for strategic interventions to restore growth momentum. `;
    } else {
      analysis += `Weekly performance has maintained stability over recent periods, providing a consistent operational baseline. `;
    }
    
    // Profitability context in weekly perspective
    analysis += `Profit margins of ${formatPercent(profitRatio)} `;
    if (wowAnalysis.performanceCategory === 'exceptional' || wowAnalysis.performanceCategory === 'strong') {
      analysis += `support the strong weekly revenue performance, demonstrating effective scaling and operational efficiency. `;
    } else if (wowAnalysis.performanceCategory === 'concerning') {
      analysis += `compound the revenue challenges, requiring comprehensive operational and strategic review. `;
    } else {
      analysis += `reflect typical weekly operational performance levels. `;
    }
    
    // Strategic implications for weekly performance
    if (wowAnalysis.performanceCategory === 'exceptional') {
      analysis += `This top-decile weekly performance indicates successful execution of growth strategies and operational excellence that should be sustained and expanded.`;
    } else if (wowAnalysis.performanceCategory === 'concerning') {
      analysis += `The bottom-quartile weekly performance signals urgent need for operational assessment and strategic pivots to restore competitive positioning.`;
    } else if (wowAnalysis.performanceCategory === 'strong') {
      analysis += `The strong weekly results demonstrate effective execution with clear opportunities to scale successful practices for consistent top-tier performance.`;
    } else {
      analysis += `The steady weekly performance provides operational stability with potential to implement targeted growth initiatives based on peak-performing week patterns.`;
    }
    
    return analysis.trim();
  };

  // ENHANCED: Generate comprehensive analysis text for DATE preset
  const generateComprehensiveDateAnalysis = (dateContext, revenue, profitRatio, trend, comparisonContext, topPerformers, extended) => {
    if (!extended) return 'Extended analysis unavailable.';
    
    let analysis = '';
    
    // Opening with comprehensive context
    analysis += `${dateContext} (${extended.dayName}) delivered ${extended.performanceInsight} with ${formatCurrency(revenue)} in revenue, `;
    
    // Trend and comparison context
    if (trend.direction !== 'stable' && trend.magnitude !== 'no-data') {
      analysis += `marking a ${trend.magnitude} ${trend.direction === 'up' ? 'increase' : 'decline'} of ${formatPercent(trend.percent)} ${comparisonContext}. `;
    } else {
      analysis += `maintaining stable performance ${comparisonContext}. `;
    }
    
    // Business day and seasonal context
    analysis += `This performance is ${extended.businessContext} and reflects ${extended.seasonalContext} for ${extended.monthName}. `;
    
    // Profitability deep dive
    analysis += `Profit margins of ${formatPercent(profitRatio)} indicate ${extended.profitabilityInsight}. `;
    
    // Product mix strategic insight
    if (extended.productMixInsight) {
      analysis += `Revenue distribution was ${extended.productMixInsight}. `;
    }
    
    // Strategic implications and recommendations
    if (extended.performanceCategory === 'exceptional') {
      analysis += `This standout performance suggests successful execution of product positioning and customer engagement strategies that should be analyzed and replicated.`;
    } else if (extended.performanceCategory === 'concerning') {
      analysis += `The underperformance warrants immediate investigation into operational factors, competitive pressures, or market conditions that may have impacted sales.`;
    } else if (extended.performanceCategory === 'strong') {
      analysis += `The above-average results indicate effective business execution with opportunities to scale successful practices across operations.`;
    } else if (extended.performanceCategory === 'weak') {
      analysis += `The below-target performance suggests need for tactical adjustments in product mix, pricing, or operational efficiency to return to optimal levels.`;
    } else {
      analysis += `The consistent performance demonstrates operational stability with potential to optimize specific areas for enhanced results.`;
    }
    
    return analysis.trim();
  };

  if (!hasData) {
    return (
      <motion.div 
        className="bg-brand-card rounded-card p-6 shadow-card mx-6 mb-4"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.16, delay: 0.2 }}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-brand-muted2/20 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiBarChart3} className="w-4 h-4 text-brand-muted2" />
            </div>
            <h3 className="text-lg font-semibold text-brand-fg">Performance Summary</h3>
          </div>
        </div>
        <div className="text-center py-8">
          <p className="text-brand-muted text-sm">Upload sales data to see IBISInsight-powered performance analysis</p>
        </div>
      </motion.div>
    );
  }

  const getTrendIcon = () => {
    if (!analysisData?.trend) return FiMinus;
    switch (analysisData.trend.direction) {
      case 'up': return FiTrendingUp;
      case 'down': return FiTrendingDown;
      default: return FiMinus;
    }
  };

  const getTrendColor = () => {
    if (!analysisData?.trend) return 'text-brand-muted';
    switch (analysisData.trend.direction) {
      case 'up': return 'text-brand-good';
      case 'down': return 'text-brand-bad';
      default: return 'text-brand-muted';
    }
  };

  const getPerformanceBadge = () => {
    // Enhanced badges for DoW vs DoW and WoW vs WoW analysis
    let badgeData = null;
    
    if (comparativeAnalysis && compare === 'DOW') {
      // DoW vs DoW badge
      const { performanceCategory, percentileRank } = comparativeAnalysis;
      badgeData = {
        category: performanceCategory,
        text: `${percentileRank}th Percentile`,
        icon: percentileRank >= 75 ? FiTarget : percentileRank >= 50 ? FiTrendingUp : FiAlert
      };
    } else if (comparativeAnalysis && compare === 'WOW') {
      // WoW vs WoW badge
      const { performanceCategory, percentileRank } = comparativeAnalysis;
      badgeData = {
        category: performanceCategory,
        text: `${percentileRank}th Percentile`,
        icon: percentileRank >= 75 ? FiTarget : percentileRank >= 50 ? FiTrendingUp : FiAlert
      };
    } else if (extendedAnalysis && preset === 'DATE') {
      // Regular DATE preset badge
      const { performanceCategory } = extendedAnalysis;
      badgeData = {
        category: performanceCategory,
        text: performanceCategory.charAt(0).toUpperCase() + performanceCategory.slice(1).replace('-', ' '),
        icon: performanceCategory === 'exceptional' ? FiTarget : performanceCategory === 'strong' ? FiTrendingUp : FiAlert
      };
    }
    
    if (!badgeData) return null;
    
    let badgeColor;
    switch (badgeData.category) {
      case 'exceptional':
        badgeColor = 'bg-brand-good/20 text-brand-good border-brand-good/30';
        break;
      case 'strong':
      case 'above-average':
        badgeColor = 'bg-brand-good/15 text-brand-good border-brand-good/25';
        break;
      case 'concerning':
      case 'below-average':
        badgeColor = 'bg-brand-bad/20 text-brand-bad border-brand-bad/30';
        break;
      case 'weak':
        badgeColor = 'bg-brand-bad/15 text-brand-bad border-brand-bad/25';
        break;
      default:
        badgeColor = 'bg-brand-accent/20 text-brand-accent border-brand-accent/30';
    }
    
    return (
      <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium border ${badgeColor}`}>
        <SafeIcon icon={badgeData.icon} className="w-3 h-3" />
        <span>{badgeData.text}</span>
      </div>
    );
  };

  return (
    <motion.div 
      className="bg-brand-card rounded-card p-6 shadow-card mx-6 mb-4"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.16, delay: 0.2 }}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-brand-accent/20 rounded-lg flex items-center justify-center">
            <SafeIcon icon={FiBarChart3} className="w-4 h-4 text-brand-accent" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-brand-fg">Performance Summary</h3>
            <p className="text-sm text-brand-muted">
              {compare === 'DOW' && preset === 'DATE' ? 'DoW vs DoW analysis with 12 similar days' :
               compare === 'WOW' && preset === 'WEEK' ? '12-week comparative analysis' :
               preset === 'DATE' ? 'Comprehensive daily analysis with business context' : 
               `IBISInsight-powered analysis for ${preset.toLowerCase()} performance`}
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          {/* Enhanced Performance Badge */}
          {getPerformanceBadge()}
          
          {/* Comparison Type Indicator */}
          {compare === 'DOW' && comparativeAnalysis && (
            <div className="flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium bg-brand-accent/10 text-brand-accent border border-brand-accent/20">
              <SafeIcon icon={FiRepeat} className="w-3 h-3" />
              <span>vs {comparativeAnalysis.comparisonCount} {comparativeAnalysis.dayName}s</span>
            </div>
          )}
          
          {compare === 'WOW' && comparativeAnalysis && (
            <div className="flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium bg-brand-accent/10 text-brand-accent border border-brand-accent/20">
              <SafeIcon icon={FiClock} className="w-3 h-3" />
              <span>vs {comparativeAnalysis.comparisonCount} weeks</span>
            </div>
          )}
          
          {/* Trend Indicator */}
          {analysisData && analysisData.trend.direction !== 'stable' && (
            <div className={`flex items-center space-x-1 ${getTrendColor()}`}>
              <SafeIcon icon={getTrendIcon()} className="w-4 h-4" />
              <span className="text-sm font-medium">
                {analysisData.trend.percent > 0 ? `${formatPercent(analysisData.trend.percent)}` : ''}
              </span>
            </div>
          )}
          
          {/* Expand/Collapse Button */}
          <motion.button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 hover:bg-brand-muted2/20 rounded-button transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <SafeIcon icon={isExpanded ? FiEyeOff : FiEye} className="w-4 h-4 text-brand-muted" />
          </motion.button>
        </div>
      </div>

      {/* Analysis Content */}
      <motion.div
        initial={{ height: isExpanded ? 'auto' : 0, opacity: isExpanded ? 1 : 0 }}
        animate={{ height: isExpanded ? 'auto' : 0, opacity: isExpanded ? 1 : 0 }}
        transition={{ duration: 0.3 }}
        style={{ overflow: 'hidden' }}
      >
        {analyzing ? (
          <div className="flex items-center space-x-3 py-4">
            <div className="w-5 h-5 border-2 border-brand-accent border-t-transparent rounded-full animate-spin" />
            <span className="text-brand-muted text-sm">Analyzing performance patterns...</span>
          </div>
        ) : analysisData ? (
          <div>
            {/* Enhanced Key Metrics Bar */}
            <div className={`grid ${compare === 'DOW' || compare === 'WOW' ? 'grid-cols-2 md:grid-cols-6' : preset === 'DATE' ? 'grid-cols-2 md:grid-cols-5' : 'grid-cols-2 md:grid-cols-4'} gap-4 mb-4 p-4 bg-brand-muted2/10 rounded-lg`}>
              <div className="text-center">
                <p className="text-brand-muted text-xs uppercase tracking-wide">Revenue</p>
                <p className="text-brand-fg font-semibold">{formatCurrency(analysisData.revenue)}</p>
                {analysisData.revenueDelta !== null && (
                  <p className={`text-xs ${getTrendColor()}`}>
                    {analysisData.revenueDelta > 0 ? '+' : ''}{formatPercent(analysisData.revenueDeltaPercent)}
                  </p>
                )}
              </div>
              
              <div className="text-center">
                <p className="text-brand-muted text-xs uppercase tracking-wide">Profit Margin</p>
                <p className="text-brand-fg font-semibold">{formatPercent(analysisData.profitRatio)}</p>
                {analysisData.profitDelta !== null && (
                  <p className={`text-xs ${analysisData.profitDelta > 0 ? 'text-brand-good' : analysisData.profitDelta < 0 ? 'text-brand-bad' : 'text-brand-muted'}`}>
                    {analysisData.profitDelta > 0 ? '+' : ''}{formatPercent(Math.abs(analysisData.profitDelta))}
                  </p>
                )}
              </div>
              
              <div className="text-center">
                <p className="text-brand-muted text-xs uppercase tracking-wide">Trend</p>
                <div className={`flex items-center justify-center space-x-1 ${getTrendColor()}`}>
                  <SafeIcon icon={getTrendIcon()} className="w-3 h-3" />
                  <span className="text-sm font-medium capitalize">
                    {analysisData.trend.magnitude !== 'no-data' ? analysisData.trend.magnitude : 'Stable'}
                  </span>
                </div>
              </div>
              
              <div className="text-center">
                <p className="text-brand-muted text-xs uppercase tracking-wide">Top Product</p>
                <p className="text-brand-fg font-semibold text-xs truncate" title={analysisData.topPerformer?.name}>
                  {analysisData.topPerformer?.name || 'N/A'}
                </p>
                {analysisData.topPerformer && (
                  <p className="text-brand-accent text-xs">
                    {formatPercent(analysisData.topPerformer.percent)}
                  </p>
                )}
              </div>
              
              {/* Additional metrics for comparative analysis */}
              {comparativeAnalysis && (compare === 'DOW' || compare === 'WOW') && (
                <>
                  <div className="text-center">
                    <p className="text-brand-muted text-xs uppercase tracking-wide">Percentile</p>
                    <p className="text-brand-fg font-semibold text-xs">
                      {comparativeAnalysis.percentileRank}th
                    </p>
                    <p className="text-brand-muted2 text-xs">
                      of {comparativeAnalysis.comparisonCount}
                    </p>
                  </div>
                  
                  <div className="text-center">
                    <p className="text-brand-muted text-xs uppercase tracking-wide">
                      {compare === 'DOW' ? 'DoW Pattern' : 'Weekly Pattern'}
                    </p>
                    <p className="text-brand-fg font-semibold text-xs">
                      {compare === 'DOW' ? comparativeAnalysis.consistencyPattern : comparativeAnalysis.weeklyConsistencyPattern}
                    </p>
                    <p className="text-brand-muted2 text-xs">
                      {formatPercent(comparativeAnalysis.coefficientOfVariation)} volatility
                    </p>
                  </div>
                </>
              )}
              
              {/* Day Type for DATE preset */}
              {preset === 'DATE' && extendedAnalysis && !comparativeAnalysis && (
                <div className="text-center">
                  <p className="text-brand-muted text-xs uppercase tracking-wide">Day Type</p>
                  <p className="text-brand-fg font-semibold text-xs">
                    {extendedAnalysis.dayName}
                  </p>
                  <p className="text-brand-muted2 text-xs">
                    {extendedAnalysis.isWeekend ? 'Weekend' : 'Weekday'}
                  </p>
                </div>
              )}
            </div>

            {/* Enhanced Analysis Text */}
            <div className="bg-brand-muted2/5 rounded-lg p-4 border-l-4 border-brand-accent">
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-brand-accent/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <SafeIcon icon={FiCalendar} className="w-3 h-3 text-brand-accent" />
                </div>
                <div className="flex-1">
                  <p className="text-brand-fg text-sm leading-relaxed">
                    {analysisData.text}
                  </p>
                  
                  {/* Enhanced Context Footer */}
                  <div className="mt-3 pt-3 border-t border-brand-muted2/20">
                    <div className="space-y-2 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="text-brand-muted">
                          Analysis for {analysisData.dateContext}
                        </span>
                        <span className="text-brand-muted2">
                          Comparison: {analysisData.comparisonContext}
                        </span>
                      </div>
                      
                      {/* DoW vs DoW context */}
                      {comparativeAnalysis && compare === 'DOW' && (
                        <div className="flex items-center justify-between text-brand-muted2">
                          <span>
                            Historical Range: {formatCurrency(comparativeAnalysis.minHistoricalRevenue)} - {formatCurrency(comparativeAnalysis.maxHistoricalRevenue)}
                          </span>
                          <span>
                            Trend: {comparativeAnalysis.seasonalTrend}
                          </span>
                        </div>
                      )}
                      
                      {/* WoW vs WoW context */}
                      {comparativeAnalysis && compare === 'WOW' && (
                        <div className="flex items-center justify-between text-brand-muted2">
                          <span>
                            Weekly Range: {formatCurrency(comparativeAnalysis.minHistoricalRevenue)} - {formatCurrency(comparativeAnalysis.maxHistoricalRevenue)}
                          </span>
                          <span>
                            Quarterly Trend: {comparativeAnalysis.quarterlyTrend}
                          </span>
                        </div>
                      )}
                      
                      {/* Regular DATE context */}
                      {preset === 'DATE' && extendedAnalysis && !comparativeAnalysis && (
                        <div className="flex items-center justify-between text-brand-muted2">
                          <span>
                            Context: {extendedAnalysis.seasonalContext}
                          </span>
                          <span>
                            Category: {extendedAnalysis.performanceCategory}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="text-brand-bad text-sm">Unable to generate analysis</p>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default PerformanceSummary;